'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function DudakDolgusuTeknikleri() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar';
  const pageTitle = 'Dudak Dolgusu Teknikleri: Doğal, Simetrik ve Estetik Sonuçlar';
  const pageDescription = 'Dudak dolgusu nasıl yapılır? Russian Lips, Paris Lips, Volume Lips gibi tekniklerin farkları ve yüz tipine göre dolgu seçimi.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Dudak Dolgusu Teknikleri', href: '/tr/blog/dudak-dolgusu-teknikleri-dogal-simetrik-ve-estetik-sonuclar' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Dudak dolgusu teknikleri ve uygulama süreci"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Dudak Estetiği
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Dudak Dolgusu Teknikleri: Doğal, Simetrik ve Estetik Sonuçlar
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>23 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Dudak dolgusu, yüz estetiğinde en çok tercih edilen uygulamalardan biridir.
                Ancak doğal ve estetik bir görünüm elde etmek yalnızca dolgu maddesine değil, uygulanan tekniğe bağlıdır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Doğru teknik, dudakların yüz oranına uygun, doğal ve simetrik görünmesini sağlar.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dudak Dolgusu Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dudak dolgusu, hyaluronik asit içeren özel jellerin dudak dokusuna enjekte edilmesiyle yapılan ameliyatsız bir işlemdir.
              </p>

              <div className="bg-pink-50 rounded-xl p-6 mb-6">
                <p className="text-pink-800 mb-4 font-medium">
                  Amaç:
                </p>
                <ul className="list-disc list-inside space-y-2 text-pink-800">
                  <li>hacim kazandırmak,</li>
                  <li>şekli belirginleştirmek,</li>
                  <li>asimetriyi düzeltmek,</li>
                  <li>dudak hattını nemlendirmektir.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Dudak Dolgusu Teknikleri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-pink-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Teknik</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Özellik</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Kimlere Uygun</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Russian Lips</td>
                      <td className="border border-gray-300 px-4 py-3">Dikey mikro enjeksiyonlarla hacim kazandırılır</td>
                      <td className="border border-gray-300 px-4 py-3">Üst dudakta kalkık görünüm</td>
                      <td className="border border-gray-300 px-4 py-3">İnce dudaklı kişiler</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Paris Lips</td>
                      <td className="border border-gray-300 px-4 py-3">Dudak konturuna paralel enjeksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Belirgin hatlar, feminen form</td>
                      <td className="border border-gray-300 px-4 py-3">Orantılı dudak yapısı</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Volume Lips</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgun, üç boyutlu etki</td>
                      <td className="border border-gray-300 px-4 py-3">Maksimum hacim</td>
                      <td className="border border-gray-300 px-4 py-3">Genç ve kalın dudak sevenler</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Natural Line Technique</td>
                      <td className="border border-gray-300 px-4 py-3">Dudak sınırına az doz enjeksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Nemli, doğal görünüm</td>
                      <td className="border border-gray-300 px-4 py-3">Doğallık isteyenler</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hybrid Technique</td>
                      <td className="border border-gray-300 px-4 py-3">Üst ve alt dudakta farklı yoğunluk</td>
                      <td className="border border-gray-300 px-4 py-3">Oranlı ve dengeli görünüm</td>
                      <td className="border border-gray-300 px-4 py-3">Asimetrik dudaklarda</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Süreci
              </h2>
              
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside space-y-3 text-purple-800">
                  <li>Dudak çevresi antiseptik ile temizlenir.</li>
                  <li>Lokal anestezik krem uygulanır (15–20 dakika beklenir).</li>
                  <li>Uygun teknik ve dolgu tipi belirlenir.</li>
                  <li>Enjeksiyon mikro kanüllerle yapılır.</li>
                  <li>İşlem sonrası soğuk kompres uygulanır.</li>
                </ol>
                <p className="text-purple-800 mt-4 font-medium">
                  Süre: yaklaşık 25–30 dakika.
                </p>
                <p className="text-purple-800">
                  Sonuçlar anında görünür, ancak 2–3 gün içinde ödem azaldıkça doğal form oturur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Dolgu Seçimi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Kullanılan dolgu genellikle hyaluronik asit (HA) temellidir.
              </p>

              <div className="bg-indigo-50 rounded-xl p-6 mb-6">
                <p className="text-indigo-800 mb-4 font-medium">
                  Markalar arasında:
                </p>
                <ul className="list-disc list-inside space-y-2 text-indigo-800">
                  <li>Juvederm Volbella / Volift,</li>
                  <li>Restylane Kysse,</li>
                  <li>Teosyal Kiss,</li>
                  <li>Belotero Intense en çok tercih edilenlerdir.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Doğal Görünüm İçin Altın Oran
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  Yüz oranlarına göre ideal dudak hacmi:
                </p>
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Alt dudak, üst dudağın %40–50 oranında daha dolgun olmalıdır.</li>
                  <li>Dudak köşeleri yanak çizgisiyle aynı hizada olmalıdır.</li>
                  <li>Filtrum mesafesi (burun altı) kişisel orana göre korunmalıdır.</li>
                </ul>
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-900 font-medium">
                    💬 Amaç "büyük dudak" değil, "yüze uygun dudak"tır.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Sonrası Bakım
              </h2>
              
              <div className="bg-amber-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-amber-600 mr-2">✅</span>
                      <span className="text-amber-800">İlk 24 saat öpüşmek, sıcak duş, makyaj yasaktır.</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-amber-600 mr-2">✅</span>
                      <span className="text-amber-800">48 saat sauna ve buhar banyosundan kaçınılmalıdır.</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className="text-amber-600 mr-2">✅</span>
                      <span className="text-amber-800">İlk hafta dudak masajı yapılmamalıdır.</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-amber-600 mr-2">✅</span>
                      <span className="text-amber-800">Günde bol su içmek dolgunun homojen dağılmasına yardımcı olur.</span>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Olası Yan Etkiler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif ödem</td>
                      <td className="border border-gray-300 px-4 py-3">1–2 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Normal reaksiyon</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Morarma</td>
                      <td className="border border-gray-300 px-4 py-3">2–3 gün</td>
                      <td className="border border-gray-300 px-4 py-3">Soğuk kompresle azalır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Asimetri</td>
                      <td className="border border-gray-300 px-4 py-3">7 gün içinde düzelir</td>
                      <td className="border border-gray-300 px-4 py-3">Doku ödemine bağlı</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <p className="text-blue-800 font-medium">
                  Kullanılan ürün orijinal ve işlem hekim kontrolündeyse kalıcı yan etki riski yoktur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8. Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Dudak dolgusunda amaç, yüz ifadesini bozmadan doğallığı korumaktır.
                  Russian veya Paris tekniği gibi modern yöntemlerle, dudak formu kişiye özel şekilde tasarlanmalıdır."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dudak dolgusu ne kadar kalıcıdır?</h3>
                  <p className="text-gray-600">Genellikle 9–12 ay sürer, kişisel metabolizmaya göre değişir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Dolgu erirse dudak eski haline döner mi?</h3>
                  <p className="text-gray-600">Evet, dolgu tamamen vücut tarafından emilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Dudak dolgusu ağrılı mı?</h3>
                  <p className="text-gray-600">Hayır. Anestezik kremle konforlu şekilde yapılır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Dudak dolgusunun zararı var mı?</h3>
                  <p className="text-gray-600">Orijinal ürün ve doğru teknikle yapıldığında zararsızdır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Lip Augmentation Techniques and Global Trends"</li>
                  <li>FDA – "Approved Hyaluronic Acid Fillers for Lips"</li>
                  <li>Harvard Health Publishing – "Modern Lip Filler Methods"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Dudak Dolgusu Uygulama Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#dudakdolgu</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#russianlips</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#parislips</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#doğalgörünüm</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-pink-600 to-rose-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Dudak Dolgusu Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan dudak dolgusu teknikleri hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Dudak dolgusu hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-pink-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudaga-botoks-uygulamasi-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudağa Botoks Uygulaması</h4>
              <p className="text-gray-600 text-sm">Dudak bölgesine botoks uygulaması nasıl yapılır? Etkileri ve uygulama süreci.</p>
            </a>
            <a
              href="/tr/blog/alin-bolgesine-dolgu-uygulamasi-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Alın Bölgesine Dolgu Uygulaması</h4>
              <p className="text-gray-600 text-sm">Alın bölgesine dolgu uygulaması nasıl yapılır? Etkileri ve uygulama süreci.</p>
            </a>
            <a
              href="/tr/blog/burun-dolgusu-ile-ameliyatsiz-burun-duzeltme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Burun Dolgusu ile Ameliyatsız Burun Düzeltme</h4>
              <p className="text-gray-600 text-sm">Burun dolgusu ile ameliyatsız burun düzeltme işlemi hakkında detaylı bilgiler.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
